import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function LeadsOverview() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);

  useEffect(() => {
    if (!companyId || companyId === 'holding') return;
    fetch(`${API}/companies/${companyId}/leads/overview`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [companyId]);

  if (!companyId || companyId === 'holding') {
    return <div style={{ padding: 32 }}><h2>Select a company to view Leads Overview</h2></div>;
  }

  if (initialLoading) return <div style={{ padding: 32, color: '#6b7280' }}>Loading dashboard...</div>;
  if (!stats) return <div style={{ padding: 32, color: '#ef4444' }}>Failed to load overview data.</div>;

  const fmt = v => '$' + Number(v || 0).toLocaleString();

  // ── Summary cards ──
  const summaryCards = [
    { label: 'Total Leads', value: stats.total_leads, color: '#3b82f6', icon: '👥', path: '/company/leads/directory' },
    { label: 'New Leads', value: stats.new_leads, color: '#8b5cf6', icon: '🆕' },
    { label: 'Contacted', value: stats.contacted_leads, color: '#0ea5e9', icon: '📞' },
    { label: 'Qualified', value: stats.qualified_leads, color: '#10b981', icon: '✅' },
    { label: 'Active Leads', value: stats.active_leads, color: '#22c55e', icon: '🟢' },
    { label: 'Stale Leads', value: stats.stale_leads, color: '#f59e0b', icon: '⏳' },
    { label: 'Total Opportunities', value: stats.total_opportunities, color: '#6366f1', icon: '🎯', path: '/company/leads/pipeline' },
    { label: 'Active Opportunities', value: stats.active_opportunities, color: '#6366f1', icon: '📊' },
    { label: 'Won', value: stats.won_opportunities, color: '#22c55e', icon: '🏆' },
    { label: 'Lost', value: stats.lost_opportunities, color: '#ef4444', icon: '❌' },
    { label: 'Conversion Rate', value: stats.conversion_rate + '%', color: '#8b5cf6', icon: '📈' },
    { label: 'Pipeline Value', value: fmt(stats.total_pipeline_value), color: '#0ea5e9', icon: '💰' },
    { label: 'Won Value', value: fmt(stats.won_value), color: '#22c55e', icon: '💵' },
  ];

  // ── Follow-up cards ──
  const followUpCards = [
    { label: 'Due Today', value: stats.follow_ups_due_today || 0, color: '#f59e0b', icon: '📅' },
    { label: 'Due This Week', value: stats.follow_ups_due_week || 0, color: '#3b82f6', icon: '📆' },
    { label: 'Overdue Activities', value: stats.overdue_activities || 0, color: '#ef4444', icon: '🚨' },
    { label: 'Upcoming Follow-Ups', value: stats.upcoming_follow_ups || 0, color: '#10b981', icon: '🔔' },
  ];

  // ── Proposal cards ──
  const proposalCards = [
    { label: 'Draft', value: stats.draft_proposals || 0, color: '#9ca3af' },
    { label: 'Awaiting Approval', value: stats.proposals_awaiting_approval || 0, color: '#f59e0b' },
    { label: 'Sent', value: stats.proposals_sent || 0, color: '#3b82f6' },
    { label: 'Awaiting Response', value: stats.proposals_awaiting_response || 0, color: '#6366f1' },
    { label: 'Accepted', value: stats.proposals_accepted || 0, color: '#22c55e' },
    { label: 'Rejected', value: stats.proposals_rejected || 0, color: '#ef4444' },
    { label: 'Expired', value: stats.proposals_expired || 0, color: '#9ca3af' },
  ];

  const pipelineStages = Array.isArray(stats.pipeline_stages) ? stats.pipeline_stages : [];
  const sourcePerf = Array.isArray(stats.source_performance) ? stats.source_performance : [];
  const ownerPerf = Array.isArray(stats.owner_performance) ? stats.owner_performance : [];
  const upcomingClosures = Array.isArray(stats.upcoming_closures) ? stats.upcoming_closures : [];
  const pendingHandoffs = Array.isArray(stats.pending_handoffs) ? stats.pending_handoffs : [];
  const recentActivity = Array.isArray(stats.recent_activity) ? stats.recent_activity : [];

  const sectionTitle = { fontSize: 17, fontWeight: 700, marginBottom: 14, color: '#1f2937' };
  const cardBase = { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,.08)' };
  const thStyle = { padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap', fontSize: 13 };
  const tdStyle = { padding: '10px 14px', fontSize: 13, borderTop: '1px solid #f3f4f6' };

  const quickLinks = [
    { label: '📋 Lead Directory', path: '/company/leads/directory' },
    { label: '📊 Pipeline', path: '/company/leads/pipeline' },
    { label: '📅 Activities', path: '/company/leads/activities' },
    { label: '📄 Proposals', path: '/company/leads/proposals' },
    { label: '🏷️ Lead Sources', path: '/company/leads/sources' },
    { label: '⚙️ Settings', path: '/company/leads/settings' },
    { label: '📜 Audit History', path: '/company/leads/audit' },
  ];

  const actionColors = { created: '#22c55e', updated: '#3b82f6', deleted: '#ef4444', stage_changed: '#8b5cf6', status_changed: '#f59e0b', owner_changed: '#6366f1', archived: '#9ca3af', restored: '#10b981', won: '#22c55e', lost: '#ef4444', reopened: '#3b82f6' };

  return (
    <div style={{ padding: 32, maxWidth: 1400 }}>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24, color: '#111827' }}>Leads Overview</h2>

      {/* ── Section 1: Summary Cards ── */}
      <div style={sectionTitle}>Summary</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(185px, 1fr))', gap: 14, marginBottom: 28 }}>
        {summaryCards.map((c, i) => (
          <div key={i} onClick={() => c.path && navigate(c.path)}
            style={{ ...cardBase, padding: '16px 18px', borderLeft: `4px solid ${c.color}`, cursor: c.path ? 'pointer' : 'default' }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{c.icon} {c.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* ── Section 2: Follow-Up Overview ── */}
      <div style={sectionTitle}>Follow-Up Overview</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
        {followUpCards.map((c, i) => (
          <div key={i} onClick={() => navigate('/company/leads/activities')}
            style={{ ...cardBase, padding: '16px 18px', borderLeft: `4px solid ${c.color}`, cursor: 'pointer' }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{c.icon} {c.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* ── Section 3: Proposal Overview ── */}
      <div style={sectionTitle}>Proposal Overview</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 28 }}>
        {proposalCards.map((c, i) => (
          <div key={i} onClick={() => navigate('/company/leads/proposals')}
            style={{ ...cardBase, padding: '14px 16px', borderTop: `3px solid ${c.color}`, cursor: 'pointer', textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: c.color }}>{c.value}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* ── Section 4: Pipeline Breakdown ── */}
      {pipelineStages.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={sectionTitle}>Pipeline by Stage</div>
          <div style={{ ...cardBase, padding: 20 }}>
            {/* Bar chart */}
            <div style={{ display: 'flex', height: 32, borderRadius: 6, overflow: 'hidden', marginBottom: 16 }}>
              {pipelineStages.map((s, i) => (
                s.percentage > 0 ? (
                  <div key={i} title={`${s.name}: ${fmt(s.value)} (${s.percentage}%)`}
                    style={{ width: s.percentage + '%', background: s.color || '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 600, minWidth: s.percentage > 5 ? 'auto' : 0 }}>
                    {s.percentage >= 8 ? s.percentage + '%' : ''}
                  </div>
                ) : null
              ))}
              {pipelineStages.every(s => s.percentage === 0) && (
                <div style={{ width: '100%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>No pipeline data</div>
              )}
            </div>
            {/* Legend */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
              {pipelineStages.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, background: s.color || '#6366f1', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#374151' }}>{s.name}</span>
                  <span style={{ fontSize: 13, color: '#6b7280', marginLeft: 'auto' }}>{s.count} opps · {fmt(s.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Section 5: Source Performance ── */}
      {sourcePerf.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={sectionTitle}>Lead Source Performance</div>
          <div style={{ ...cardBase, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Source', 'Total Leads', 'Qualified', 'Won', 'Conv. Rate', 'Expected Value', 'Won Value'].map((h, i) => (
                    <th key={i} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sourcePerf.map(s => (
                  <tr key={s.id}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{s.name}</td>
                    <td style={tdStyle}>{s.total_leads}</td>
                    <td style={tdStyle}>{s.qualified_leads}</td>
                    <td style={tdStyle}>{s.won_leads}</td>
                    <td style={tdStyle}>
                      <span style={{ color: s.conversion_rate >= 30 ? '#22c55e' : s.conversion_rate >= 15 ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>
                        {s.conversion_rate}%
                      </span>
                    </td>
                    <td style={tdStyle}>{fmt(s.expected_value)}</td>
                    <td style={tdStyle}>{fmt(s.won_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Section 6: Salesperson Performance ── */}
      {ownerPerf.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={sectionTitle}>Salesperson Performance</div>
          <div style={{ ...cardBase, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Owner', 'Active', 'New', 'Qualified', 'Follow-Ups Due', 'Overdue', 'Won', 'Lost', 'Conv. Rate', 'Pipeline Value', 'Won Value'].map((h, i) => (
                    <th key={i} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ownerPerf.map(o => (
                  <tr key={o.owner_id}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{o.owner_name}</td>
                    <td style={tdStyle}>{o.active_leads}</td>
                    <td style={tdStyle}>{o.new_leads}</td>
                    <td style={tdStyle}>{o.qualified_leads}</td>
                    <td style={tdStyle}>{o.follow_ups_due}</td>
                    <td style={tdStyle}>
                      <span style={{ color: o.overdue_follow_ups > 0 ? '#ef4444' : '#22c55e', fontWeight: o.overdue_follow_ups > 0 ? 700 : 400 }}>
                        {o.overdue_follow_ups}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, color: '#22c55e', fontWeight: 600 }}>{o.won_leads}</td>
                    <td style={{ ...tdStyle, color: '#ef4444' }}>{o.lost_leads}</td>
                    <td style={tdStyle}>
                      <span style={{ color: o.conversion_rate >= 40 ? '#22c55e' : o.conversion_rate >= 20 ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>
                        {o.conversion_rate}%
                      </span>
                    </td>
                    <td style={tdStyle}>{fmt(o.pipeline_value)}</td>
                    <td style={tdStyle}>{fmt(o.won_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Section 7: Upcoming Closures ── */}
      {upcomingClosures.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={sectionTitle}>Upcoming Closures (Next 30 Days)</div>
          <div style={{ ...cardBase, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Opportunity', 'Lead', 'Module / Service', 'Owner', 'Stage', 'Expected Value', 'Closing Date', 'Status'].map((h, i) => (
                    <th key={i} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {upcomingClosures.map(c => (
                  <tr key={c.id}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{c.name}</td>
                    <td style={tdStyle}>
                      <span style={{ color: '#6366f1', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => navigate(`/company/leads/${c.lead_id}`)}>
                        {c.lead_name}
                      </span>
                    </td>
                    <td style={tdStyle}>{[c.module, c.service].filter(Boolean).join(' / ') || '—'}</td>
                    <td style={tdStyle}>{c.owner}</td>
                    <td style={tdStyle}>{c.stage}</td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{fmt(c.expected_value)}</td>
                    <td style={tdStyle}>{c.expected_closing_date ? new Date(c.expected_closing_date).toLocaleDateString() : '—'}</td>
                    <td style={tdStyle}>
                      {c.is_overdue ? (
                        <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#fef2f2', color: '#ef4444' }}>Overdue</span>
                      ) : (
                        <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#f0fdf4', color: '#22c55e' }}>On Track</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Section 8: Pending Handoffs ── */}
      {pendingHandoffs.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={sectionTitle}>Won Opportunities — Pending Handoff</div>
          <div style={{ ...cardBase, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Lead', 'Module / Service', 'Final Value', 'Won Date', 'Handoff Status'].map((h, i) => (
                    <th key={i} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pendingHandoffs.map(h => {
                  const statusColors = { not_started: '#9ca3af', pending: '#f59e0b', in_progress: '#3b82f6', completed: '#22c55e' };
                  return (
                    <tr key={h.id}>
                      <td style={tdStyle}>
                        <span style={{ color: '#6366f1', cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => navigate(`/company/leads/${h.lead_id}`)}>
                          {h.lead_name}
                        </span>
                      </td>
                      <td style={tdStyle}>{[h.module, h.service].filter(Boolean).join(' / ') || '—'}</td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{fmt(h.final_value)}</td>
                      <td style={tdStyle}>{h.won_date ? new Date(h.won_date).toLocaleDateString() : '—'}</td>
                      <td style={tdStyle}>
                        <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: '#fff', background: statusColors[h.handoff_status] || '#9ca3af' }}>
                          {(h.handoff_status || 'not_started').replace(/_/g, ' ')}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Section 9: Recent Activity ── */}
      {recentActivity.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={sectionTitle}>Recent Activity</div>
          <div style={{ ...cardBase, padding: 0 }}>
            {recentActivity.map((a, i) => (
              <div key={a.id} style={{ display: 'flex', gap: 12, padding: '12px 18px', borderBottom: i < recentActivity.length - 1 ? '1px solid #f3f4f6' : 'none', alignItems: 'center' }}>
                <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: '#fff', background: actionColors[a.action] || '#6b7280', whiteSpace: 'nowrap' }}>
                  {a.action?.replace(/_/g, ' ')}
                </span>
                <span style={{ fontSize: 13, color: '#374151', flex: 1 }}>
                  <strong>{a.user}</strong> — {a.description || `${a.action} ${a.record_type}`}
                </span>
                <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                  {new Date(a.created_at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Quick Access Links ── */}
      <div style={sectionTitle}>Quick Access</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
        {quickLinks.map((l, i) => (
          <button key={i} onClick={() => navigate(l.path)}
            style={{
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '16px 20px',
              fontSize: 14, fontWeight: 500, cursor: 'pointer', textAlign: 'left',
            }}
            onMouseOver={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.borderColor = '#6366f1'; }}
            onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
          >
            {l.label}
          </button>
        ))}
      </div>
    </div>
  );
}
